import { Close, Send, CheckCircleOutline, BlockOutlined } from "@mui/icons-material";
import {
  Button,
  CircularProgress,
  Paper,
  TableContainer,
  TextField,
  Box,
  Typography,
  Divider,
} from "@mui/material";
import { useState, useContext } from "react";
import { useOktaAuth } from "@okta/okta-react";
import { ModuleContext } from "../../context/ModuleContext";
import { UserModuleContext } from "../../context/UserModuleContext";

const WSU_RED = "#A60F2D";
const ENABLED_GREEN = "#2e7d32";

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    "&:hover fieldset": { borderColor: WSU_RED },
    "&.Mui-focused fieldset": { borderColor: WSU_RED },
  },
  "& .MuiInputLabel-root.Mui-focused": { color: WSU_RED },
};

export const ADAccountStatus = () => {
  const [state, setState] = useState({
    error: null,
    waiting: false,
    userComment: "",
    message: null,
  });

  const { setModule } = useContext(ModuleContext);
  const { userModule, setUserModule } = useContext(UserModuleContext);
  const { authState } = useOktaAuth();

  const isEnabled = userModule.ADData["AD Account Enabled"];
  const networkId = userModule.userData["Network ID"];

  // Action is disabling if currently enabled, enabling if currently disabled
  const actionIsDisable = isEnabled;
  const actionColor = actionIsDisable ? WSU_RED : ENABLED_GREEN;

  const toggleAdStatus = async () => {
    setState((prev) => ({ ...prev, waiting: true }));

    try {
      const res = await fetch(
          `${process.env.REACT_APP_AWS_API_GATEWAY_ENDPOINT}/user/ad/status`,
          {
            method: "POST",
            headers: {
              Authorization: authState.accessToken.accessToken,
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            signal: AbortSignal.timeout(360000),
            body: JSON.stringify({
              networkId,
              comment: state.userComment,
              callerId: authState.accessToken.claims.sub,
            }),
          }
      );

      if (!res.ok) {
        const errorObj = await res.json();
        throw new Error(errorObj?.error?.message || "Server error");
      }

      setUserModule((prev) => ({
        ...prev,
        ADData: { ...prev.ADData, "AD Account Enabled": !isEnabled },
      }));

      setState((prev) => ({
        ...prev,
        error: null,
        waiting: false,
        message: `Account ${networkId} is now ${!isEnabled ? "Enabled" : "Disabled"}`,
        newStatus: !isEnabled,
      }));
    } catch (error) {
      setState((prev) => ({ ...prev, waiting: false, error }));
    }
  };

  if (state.waiting) {
    return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
          <CircularProgress style={{ color: WSU_RED }} size={80} thickness={5} />
        </Box>
    );
  }

  if (state.message) {
    return (
        <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2, overflow: "hidden" }}>
          <Box
              sx={{
                backgroundColor: state.newStatus ? ENABLED_GREEN : WSU_RED,
                color: "white",
                px: 3,
                py: 2,
                display: "flex",
                alignItems: "center",
                gap: 1.5,
              }}
          >
            {state.newStatus
                ? <CheckCircleOutline fontSize="small" />
                : <BlockOutlined fontSize="small" />
            }
            <Typography variant="subtitle2" fontWeight={700}>
              Account Status Updated
            </Typography>
          </Box>

          <Box sx={{ px: 3, pt: 3, pb: 1 }}>
            <Typography variant="overline" sx={{ color: WSU_RED, fontWeight: 700, letterSpacing: 1.5 }}>
              Confirmation
            </Typography>
          </Box>

          <Box sx={{ px: 3, py: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {state.message}
            </Typography>
          </Box>

          <Box sx={{ px: 3, pb: 4, display: "flex", justifyContent: "flex-end" }}>
            <Button
                variant="outlined"
                endIcon={<Close />}
                onClick={() => setModule((prev) => ({ ...prev, isVisible: false }))}
                sx={{ borderColor: "#ccc", color: "#666", "&:hover": { borderColor: "#aaa", backgroundColor: "#f9f9f9" } }}
            >
              Close
            </Button>
          </Box>
        </TableContainer>
    );
  }

  return (
      <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2, overflow: "hidden" }}>

        {/* Error Banner */}
        {state.error && (
            <Box sx={{ backgroundColor: WSU_RED, color: "white", px: 3, py: 2 }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
                Action failed
              </Typography>
              <Typography variant="body2">{state.error.message}</Typography>
            </Box>
        )}

        {/* ── Section: Account Status ── */}
        <Box sx={{ px: 3, pt: 3, pb: 1 }}>
          <Typography variant="overline" sx={{ color: WSU_RED, fontWeight: 700, letterSpacing: 1.5 }}>
            Account Status
          </Typography>
          <Typography variant="caption" sx={{ display: "block", color: "#777", mt: 0.25 }}>
            {networkId}
          </Typography>
        </Box>

        <Box sx={{ px: 3, py: 2 }}>
          <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 1,
                px: 2,
                py: 0.75,
                borderRadius: 2,
                backgroundColor: isEnabled ? "#e8f5e9" : "#fce4ec",
                border: `1px solid ${isEnabled ? ENABLED_GREEN : WSU_RED}`,
              }}
          >
            {isEnabled
                ? <CheckCircleOutline fontSize="small" sx={{ color: ENABLED_GREEN }} />
                : <BlockOutlined fontSize="small" sx={{ color: WSU_RED }} />
            }
            <Typography
                variant="body2"
                fontWeight={600}
                sx={{ color: isEnabled ? ENABLED_GREEN : WSU_RED }}
            >
              {isEnabled ? "Currently Enabled" : "Currently Disabled"}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ mx: 3 }} />

        {/* ── Section: Justification ── */}
        <Box sx={{ px: 3, pt: 3, pb: 1 }}>
          <Typography variant="overline" sx={{ color: WSU_RED, fontWeight: 700, letterSpacing: 1.5 }}>
            Justification
          </Typography>
        </Box>

        <Box sx={{ px: 3, pb: 3 }}>
          <TextField
              required
              fullWidth
              size="small"
              multiline
              rows={3}
              label="Reason"
              value={state.userComment}
              onChange={(e) => setState((prev) => ({ ...prev, userComment: e.target.value }))}
              placeholder={`Provide a reason for ${actionIsDisable ? "disabling" : "enabling"} this account`}
              sx={fieldSx}
          />
        </Box>

        {/* ── Actions ── */}
        <Box sx={{ px: 3, pb: 4, display: "flex", justifyContent: "flex-end", gap: 2 }}>
          <Button
              variant="outlined"
              endIcon={<Close />}
              onClick={() => setModule((prev) => ({ ...prev, isVisible: false }))}
              sx={{
                borderColor: "#ccc",
                color: "#666",
                "&:hover": { borderColor: "#aaa", backgroundColor: "#f9f9f9" },
              }}
          >
            Cancel
          </Button>
          <Button
              variant="contained"
              endIcon={<Send />}
              onClick={toggleAdStatus}
              disabled={!state.userComment.trim()}
              sx={{
                backgroundColor: actionColor,
                color: "white",
                px: 4,
                py: 1.25,
                fontWeight: 600,
                letterSpacing: 0.5,
                "&:hover": { backgroundColor: actionIsDisable ? "#8a0c25" : "#1b5e20" },
                "&.Mui-disabled": { backgroundColor: "#ddd", color: "#aaa" },
              }}
          >
            {actionIsDisable ? "Disable Account" : "Enable Account"}
          </Button>
        </Box>

      </TableContainer>
  );
};